import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { PaymentOptionsStateService } from "./payment-options.state.service";
import { map, switchMap, tap } from "rxjs/operators";
import { PaymentOption } from "../entities/payment-option.model";
import { PaymentOptionsRemote } from "@modules/+new/payments/features/payment-options/infrastructure/payment-options.remote";
import { FiatProviderDetails } from "@modules/+new/payments/features/payment-options/domain/entities/fiat-provider-details.model";
import { PaymentOptionsForFiatSummary } from "@modules/+new/payments/features/payment-options/domain/entities/payment-options-summary.model";
import { Limits } from "@modules/+new/payments/shared-payments/entities/limits.model";
import { BestFacade } from "@modules/new_account/features/my-best/domain/+state/best.facade";

@Injectable({
	providedIn: "any"
})
export class PaymentOptionsFacade {
	public constructor(
		private readonly stateService: PaymentOptionsStateService,
		private readonly remote: PaymentOptionsRemote,
		private readonly bestFacade: BestFacade
	) {
		this.clearPaymentOptionsAfterRefreshingBestReward();
	}

	public getPaymentOptionsSummaryByFiat$(fiatId: number): Observable<PaymentOptionsForFiatSummary> {
		const initialSummary: PaymentOptionsForFiatSummary = {
			fiatId,
			availableOptions: [],
			unavailableOptions: [],
			bestEligibleOptions: []
		};

		return this.getPaymentOptions$().pipe(
			map((paymentOptions: PaymentOption[]) =>
				paymentOptions.reduce((summary: PaymentOptionsForFiatSummary, option: PaymentOption) => {
					if (!this.isPaymentOptionVisible(option)) {
						return summary;
					}

					const details: FiatProviderDetails[] = this.getPaymentOptionDetailsForFiat(option, fiatId);

					if (!this.isPaymentOptionAvailableForFiat(details)) {
						summary.unavailableOptions.push(option);
						return summary;
					}

					summary.availableOptions.push(option);
					if (this.isPaymentOptionEligibleForBestDiscount(details)) {
						summary.bestEligibleOptions.push(option);
					}

					return summary;
				}, initialSummary)
			)
		);
	}

	private isPaymentOptionVisible(option: PaymentOption): boolean {
		return !option.isVoucher && option.enabled;
	}

	private getPaymentOptionDetailsForFiat(option: PaymentOption, fiatId: number): FiatProviderDetails[] {
		return option.fiatProviderDetails.filter((details: FiatProviderDetails) => details.fiatId === fiatId);
	}

	private isPaymentOptionAvailableForFiat(details: FiatProviderDetails[]): boolean {
		return !!details && details.length > 0;
	}

	private isPaymentOptionEligibleForBestDiscount(details: FiatProviderDetails[]): boolean {
		return !!details && details.some((detail: FiatProviderDetails) => detail.vipFeePercentDifference !== 0);
	}

	public getPaymentOptionById$(id: number): Observable<PaymentOption> {
		return this.getPaymentOptions$().pipe(
			map((options: PaymentOption[]) => options.find((option: PaymentOption) => option.id === id))
		);
	}

	public getPaymentOptions$(): Observable<PaymentOption[]> {
		if (this.stateService.isPaymentOptionsLoaded()) {
			return this.stateService.getPaymentOptions$();
		}

		return this.remote.getPaymentOptions$().pipe(
			tap((options: PaymentOption[]) => {
				this.stateService.updatePaymentOptions(options);
			}),
			switchMap(() => this.stateService.getPaymentOptions$())
		);
	}

	// TODO: current "bugfixer" (transferred from masterDataService + reverse dependency) - to be solved in a "civilized way"
	private clearPaymentOptionsAfterRefreshingBestReward(): void {
		this.bestFacade.getBestState$().subscribe(() => this.stateService.markPaymentOptionsAsOutdated());
	}

	public getLimits$(paymentOptionId: number, fiatId: number): Observable<Limits> {
		return this.remote.getLimit$(paymentOptionId, fiatId);
	}
}
